import {
  EXCLUDE,
  ITEM_PATHS,
  MULTIPLIER_MAX,
  MULTIPLIER_MIN,
  PROFILE_FIELD_KEY_BY_USER_PATH,
  SAFE_PAIRING,
  USER_PATHS,
} from './constants';

/**
 * Checks the backend deliberately does not perform. A rule with a mismatched
 * vocabulary or a stale value is accepted and simply never matches, so these are
 * the difference between "the rule does nothing" and knowing why.
 *
 * Each returns `null` or `{ level: 'warning' | 'note', message }`.
 */

const warning = message => ({ level: 'warning', message });
const note = message => ({ level: 'note', message });

/**
 * §3a — a userPath rule is skipped entirely for users with no value in that
 * field, which is what usually lies behind a "the rule isn't working" report.
 */
export const emptyProfileNote = (rule, fieldLabel) => {
  if (!rule.userPath) return null;
  return note(
    `If a user has no ${fieldLabel} on their profile, this rule is skipped entirely for their search — even where it excludes. An incomplete profile can never hide content from someone.`,
  );
};

const vocabularyForItemPath = (itemPath, { labels, valueOptionsFor }) =>
  itemPath === ITEM_PATHS.LABELS
    ? labels.map(label => label.code)
    : valueOptionsFor(itemPath).map(option => option.value);

/** Long lists are for recognising, not reading — a few names, then a count. */
const listCodes = (codes, limit = 6) =>
  codes.length > limit
    ? `${codes.slice(0, limit).join(', ')} and ${codes.length - limit} more`
    : codes.join(', ');

/**
 * §5 — only location↔LABELS is guaranteed to share a vocabulary. Everywhere else
 * the two sides are separate lists: what a user can be assigned is this org's
 * profile-field options, what a result can be tagged with is the item
 * vocabulary. A value present only on the user side can never appear on a
 * result, so the rule is inert for whoever holds it — name those values.
 */
export const pairingWarning = (rule, { profileFieldsByKey, labels, valueOptionsFor }) => {
  if (!rule.userPath) return null;

  if (rule.userPath === SAFE_PAIRING.userPath) {
    return rule.itemPath === SAFE_PAIRING.itemPath
      ? null
      : warning(
          `Location holds label codes, but this rule inspects ${rule.itemPath}. Those two vocabularies only coincide by accident — expect nothing to land in Match.`,
        );
  }

  const fieldKey = PROFILE_FIELD_KEY_BY_USER_PATH[rule.userPath];
  const field = profileFieldsByKey?.[fieldKey];
  const optionCodes = field?.options?.map(option => option.code) ?? [];
  const fieldLabel = field?.displayLabel ?? fieldKey;

  if (!optionCodes.length) {
    return warning(
      `This organization has no options configured for ${fieldLabel}, so no user has a value for it and the rule will never apply.`,
    );
  }

  const itemVocabulary = new Set(vocabularyForItemPath(rule.itemPath, { labels, valueOptionsFor }));
  const overlap = optionCodes.filter(code => itemVocabulary.has(code)).length;

  if (overlap === 0) {
    return warning(
      `A user's ${fieldLabel} and a result's ${rule.itemPath} are drawn from two separate lists, and none of this organization's ${optionCodes.length} ${fieldLabel} options appear in the ${rule.itemPath} one — no result can be tagged with any of them, so this rule can never match.`,
    );
  }
  if (overlap < optionCodes.length) {
    const unmatched = optionCodes.filter(code => !itemVocabulary.has(code));
    const one = unmatched.length === 1;
    return note(
      `A user's ${fieldLabel} and a result's ${rule.itemPath} are drawn from two separate lists, and ${listCodes(unmatched)} ${one ? 'exists' : 'exist'} only on the user side. No result can be tagged with ${one ? 'it' : 'them'}, so the rule has no effect for users on ${one ? 'that value' : 'those values'}. The other ${overlap} appear in both.`,
    );
  }
  return null;
};

/** A value that has since been removed from the org's vocabulary matches nothing. */
export const staleValuesWarning = (rule, valueOptions) => {
  if (!rule.values?.length) return null;
  const known = new Set(valueOptions.map(option => option.value));
  const stale = rule.values.filter(value => !known.has(value));
  if (!stale.length) return null;
  return warning(
    `${stale.join(', ')} ${stale.length === 1 ? 'is' : 'are'} not in this organization's ${rule.itemPath} vocabulary — matching is exact and case-sensitive, so ${stale.length === 1 ? 'it' : 'they'} will never match.`,
  );
};

/** A workspace that no longer exists narrows the rule's scope down to nothing. */
export const staleWorkspacesWarning = (rule, workspaceOptions) => {
  if (!rule.workspaceIds?.length) return null;
  const known = new Set(workspaceOptions.map(option => option.value));
  const stale = rule.workspaceIds.filter(id => !known.has(id));
  if (!stale.length) return null;
  const scopeLeft = rule.workspaceIds.length - stale.length;
  return warning(
    `${stale.join(', ')} ${stale.length === 1 ? 'is' : 'are'} not a workspace of this organization — the workspace was probably deleted. ${
      scopeLeft
        ? `This rule only applies in the remaining ${scopeLeft} workspace${scopeLeft === 1 ? '' : 's'}.`
        : 'This rule is scoped to nothing and will never apply.'
    }`,
  );
};

export const ruleAdvisories = (rule, context) =>
  [
    pairingWarning(rule, context),
    staleValuesWarning(rule, context.valueOptionsFor(rule.itemPath)),
    staleWorkspacesWarning(rule, context.workspaceOptions),
    emptyProfileNote(rule, context.userPathLabel(rule.userPath)),
  ].filter(Boolean);

export const hasWarning = advisories => advisories.some(a => a.level === 'warning');

const scopesOverlap = (a = [], b = []) => !a.length || !b.length || a.some(id => b.includes(id));

/**
 * §10 — rules stack multiplicatively and the combined effect is clamped, so a
 * large number is not more powerful. Reports the worst-case Match product across
 * enabled rules whose workspace scopes can overlap.
 */
export const combinedMatchMultiplier = rules => {
  const enabled = rules.filter(rule => rule.enabled !== false && rule.match !== EXCLUDE);
  const stacking = enabled.filter(rule =>
    enabled.some(other => other !== rule && scopesOverlap(rule.workspaceIds, other.workspaceIds)),
  );
  if (stacking.length < 2) return null;

  const product = stacking.reduce((acc, rule) => acc * Number(rule.match ?? 1), 1);
  const clamped = Math.min(Math.max(product, MULTIPLIER_MIN), MULTIPLIER_MAX);
  return { count: stacking.length, product, clamped, isClamped: clamped !== product };
};

export const excludingBuckets = rule =>
  ['match', 'unassigned', 'other'].filter(bucket => rule[bucket] === EXCLUDE);

/** §6 — say plainly what disappears, in the terms of this specific rule. */
export const excludeSummary = (rule, { workspaceLabel, userPathLabel }) => {
  const buckets = excludingBuckets(rule);
  if (!buckets.length) return null;

  const target =
    rule.userPath === USER_PATHS.LOCATION
      ? "the searcher's Location"
      : rule.userPath
        ? `the searcher's ${userPathLabel(rule.userPath)}`
        : (rule.values ?? []).join(', ');

  const scope = rule.workspaceIds?.length
    ? `in ${rule.workspaceIds.map(workspaceLabel).join(', ')}`
    : 'anywhere in this organization';

  const phrases = {
    match: `whose ${rule.itemPath} matches ${target}`,
    unassigned: `with no ${rule.itemPath} value at all`,
    other: `whose ${rule.itemPath} is set to something other than ${target}`,
  };

  return {
    buckets,
    lines: buckets.map(
      bucket =>
        `Results ${scope} ${phrases[bucket]} will not be returned to anyone, and will not be counted in the result count.`,
    ),
  };
};
